/**
 * Gmail Client Service
 *
 * Handles Gmail API operations: send, receive, search messages
 * Implements incremental sync with Gmail history API
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { IService, ServiceResponse } from "@/services/core/service-registry";
import { gmailAuthService } from "./gmail-auth.service";
import { integrationActivityService } from "@flowstate/core";
import type {
  GmailMessage,
  GmailMessageInsert,
  GmailSendMessageRequest,
  GmailSearchOptions,
  GmailSyncOptions,
  GmailMessageResponse,
  GmailListMessagesResponse,
  GmailAttachmentResponse,
  ParsedGmailMessage,
  EmailAddress,
  GmailHistoryResponse,
  GmailMessagePayload,
} from "@/types/gmail";
import { logger } from "@/lib/logger";

export interface IGmailClientService extends IService {
  // Message Operations
  sendMessage(userId: string, request: GmailSendMessageRequest): Promise<ServiceResponse<string>>;
  getMessage(userId: string, messageId: string): Promise<ServiceResponse<ParsedGmailMessage>>;
  listMessages(userId: string, options?: GmailSearchOptions): Promise<ServiceResponse<GmailMessage[]>>;
  deleteMessage(userId: string, messageId: string): Promise<ServiceResponse<void>>;

  // Sync Operations
  syncMessages(userId: string, options?: GmailSyncOptions): Promise<ServiceResponse<{ synced: number; failed: number }>>;
  getAttachment(userId: string, messageId: string, attachmentId: string): Promise<ServiceResponse<GmailAttachmentResponse>>;

  // Label Operations
  addLabels(userId: string, messageId: string, labelIds: string[]): Promise<ServiceResponse<void>>;
  removeLabels(userId: string, messageId: string, labelIds: string[]): Promise<ServiceResponse<void>>;
}

export class GmailClientService implements IGmailClientService {
  readonly name = "gmail-client";
  private readonly supabase = typedSupabase;
  private readonly GMAIL_API_BASE = "https://gmail.googleapis.com/gmail/v1";

  async initialize(): Promise<void> {
    logger.info("Gmail client service initialized");
  }

  async dispose(): Promise<void> {
    logger.info("Gmail client service disposed");
  }

  /**
   * Send an email via Gmail API
   */
  async sendMessage(
    userId: string,
    request: GmailSendMessageRequest
  ): Promise<ServiceResponse<string>> {
    try {
      const tokenResult = await gmailAuthService.getValidToken(userId);
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }

      const token = tokenResult.data!;

      // Construct RFC 2822 email format
      const emailLines: string[] = [];
      emailLines.push(`To: ${request.to.join(', ')}`);
      if (request.cc && request.cc.length > 0) {
        emailLines.push(`Cc: ${request.cc.join(', ')}`);
      }
      if (request.bcc && request.bcc.length > 0) {
        emailLines.push(`Bcc: ${request.bcc.join(', ')}`);
      }
      emailLines.push(`Subject: ${request.subject}`);
      if (request.replyTo) {
        emailLines.push(`Reply-To: ${request.replyTo}`);
      }
      if (request.threadId) {
        emailLines.push(`In-Reply-To: ${request.threadId}`);
        emailLines.push(`References: ${request.threadId}`);
      }
      emailLines.push('Content-Type: text/html; charset=utf-8');
      emailLines.push('');
      emailLines.push(request.bodyHtml ?? request.body);

      const email = emailLines.join('\r\n');
      const encodedEmail = btoa(unescape(encodeURIComponent(email)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      const response = await fetch(`${this.GMAIL_API_BASE}/users/me/messages/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          raw: encodedEmail,
          threadId: request.threadId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: `Failed to send email: ${error.error?.message ?? response.statusText}` };
      }

      const data = await response.json();
      logger.info('Email sent successfully', { userId, messageId: data.id });

      // Log Gmail activity
      await this.logGmailActivity(userId, data.id, 'sent', 'gmail.message_sent', {
        to_addresses: request.to,
        cc_addresses: request.cc,
        subject: request.subject,
        thread_id: request.threadId,
      });

      return { success: true, data: data.id };
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown error");
      logger.error('Failed to send email', { userId, error: err.message });
      return { success: false, error: err.message };
    }
  }

  /**
   * Get a single message with full details
   */
  async getMessage(userId: string, messageId: string): Promise<ServiceResponse<ParsedGmailMessage>> {
    try {
      const tokenResult = await gmailAuthService.getValidToken(userId);
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }

      const token = tokenResult.data!;

      const response = await fetch(
        `${this.GMAIL_API_BASE}/users/me/messages/${messageId}?format=full`,
        {
          headers: {
            'Authorization': `Bearer ${token.access_token}`,
          },
        }
      );

      if (!response.ok) {
        return { success: false, error: `Failed to fetch message: ${response.statusText}` };
      }

      const messageData: GmailMessageResponse = await response.json();
      const parsed = this.parseGmailMessage(messageData);

      return { success: true, data: parsed };
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: err.message };
    }
  }

  /**
   * List messages from local database with optional filters
   */
  async listMessages(
    userId: string,
    options?: GmailSearchOptions
  ): Promise<ServiceResponse<GmailMessage[]>> {
    try {
      let query = this.supabase
        .from('gmail_messages')
        .select('*')
        .eq('user_id', userId)
        .order('received_at', { ascending: false });

      if (options?.maxResults) {
        query = query.limit(options.maxResults);
      }

      if (options?.labelIds && options.labelIds.length > 0) {
        query = query.contains('labels', options.labelIds);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: err.message };
    }
  }

  /**
   * Delete a message (move to trash)
   */
  async deleteMessage(userId: string, messageId: string): Promise<ServiceResponse<void>> {
    try {
      const tokenResult = await gmailAuthService.getValidToken(userId);
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }

      const token = tokenResult.data!;

      const response = await fetch(
        `${this.GMAIL_API_BASE}/users/me/messages/${messageId}/trash`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token.access_token}`,
          },
        }
      );

      if (!response.ok) {
        return { success: false, error: `Failed to delete message: ${response.statusText}` };
      }

      // Update local database
      await this.supabase
        .from('gmail_messages')
        .update({ is_trash: true })
        .eq('user_id', userId)
        .eq('gmail_message_id', messageId);

      // Log Gmail activity
      await this.logGmailActivity(userId, messageId, 'deleted', 'gmail.message_deleted', {});

      return { success: true, data: undefined };
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: err.message };
    }
  }

  /**
   * Sync messages from Gmail to local database
   * Uses incremental sync with Gmail History API when possible
   */
  async syncMessages(
    userId: string,
    options?: GmailSyncOptions
  ): Promise<ServiceResponse<{ synced: number; failed: number }>> {
    try {
      const tokenResult = await gmailAuthService.getValidToken(userId);
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }

      const token = tokenResult.data!;

      // Get sync state
      const { data: syncState } = await this.supabase
        .from('gmail_sync_state')
        .select('*')
        .eq('user_id', userId)
        .single();

      let synced = 0;
      let failed = 0;

      // Update sync state to in_progress
      await this.supabase
        .from('gmail_sync_state')
        .upsert({
          user_id: userId,
          sync_status: 'in_progress',
          last_sync_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        });

      if (options?.fullSync || !syncState?.history_id) {
        // Full sync: fetch all messages
        const result = await this.performFullSync(userId, token.access_token, options);
        synced = result.synced;
        failed = result.failed;
      } else {
        // Incremental sync: use History API
        const result = await this.performIncrementalSync(
          userId,
          token.access_token,
          syncState.history_id,
          options
        );
        synced = result.synced;
        failed = result.failed;
      }

      // Update sync state
      await this.supabase
        .from('gmail_sync_state')
        .update({
          sync_status: 'success',
          messages_synced: synced,
        })
        .eq('user_id', userId);

      logger.info('Gmail sync completed', { userId, synced, failed });

      // Log sync completion activity
      await this.logGmailActivity(userId, 'sync_operation', 'sync_completed', 'gmail.sync_completed', {
        messages_synced: synced,
        messages_failed: failed,
      });

      return { success: true, data: { synced, failed } };
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown error");
      logger.error('Gmail sync failed', { userId, error: err.message });

      // Update sync state to failed
      await this.supabase
        .from('gmail_sync_state')
        .update({
          sync_status: 'failed',
          sync_error: err.message,
        })
        .eq('user_id', userId);

      return { success: false, error: err.message };
    }
  }

  /**
   * Get attachment data
   */
  async getAttachment(
    userId: string,
    messageId: string,
    attachmentId: string
  ): Promise<ServiceResponse<GmailAttachmentResponse>> {
    try {
      const tokenResult = await gmailAuthService.getValidToken(userId);
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }

      const token = tokenResult.data!;

      const response = await fetch(
        `${this.GMAIL_API_BASE}/users/me/messages/${messageId}/attachments/${attachmentId}`,
        {
          headers: {
            'Authorization': `Bearer ${token.access_token}`,
          },
        }
      );

      if (!response.ok) {
        return { success: false, error: `Failed to fetch attachment: ${response.statusText}` };
      }

      const data: GmailAttachmentResponse = await response.json();
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: err.message };
    }
  }

  /**
   * Add labels to a message
   */
  async addLabels(userId: string, messageId: string, labelIds: string[]): Promise<ServiceResponse<void>> {
    try {
      const tokenResult = await gmailAuthService.getValidToken(userId);
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }

      const token = tokenResult.data!;

      const response = await fetch(
        `${this.GMAIL_API_BASE}/users/me/messages/${messageId}/modify`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            addLabelIds: labelIds,
          }),
        }
      );

      if (!response.ok) {
        return { success: false, error: `Failed to add labels: ${response.statusText}` };
      }

      // Log Gmail activity
      await this.logGmailActivity(userId, messageId, 'labeled', 'gmail.message_labeled', {
        labels_added: labelIds,
      });

      return { success: true, data: undefined };
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: err.message };
    }
  }

  /**
   * Remove labels from a message
   */
  async removeLabels(userId: string, messageId: string, labelIds: string[]): Promise<ServiceResponse<void>> {
    try {
      const tokenResult = await gmailAuthService.getValidToken(userId);
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }

      const token = tokenResult.data!;

      const response = await fetch(
        `${this.GMAIL_API_BASE}/users/me/messages/${messageId}/modify`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            removeLabelIds: labelIds,
          }),
        }
      );

      if (!response.ok) {
        return { success: false, error: `Failed to remove labels: ${response.statusText}` };
      }

      return { success: true, data: undefined };
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown error");
      return { success: false, error: err.message };
    }
  }

  // Private helper methods

  private async performFullSync(
    userId: string,
    accessToken: string,
    options?: GmailSyncOptions
  ): Promise<{ synced: number; failed: number }> {
    let synced = 0;
    let failed = 0;
    let pageToken: string | undefined;

    do {
      const url = new URL(`${this.GMAIL_API_BASE}/users/me/messages`);
      url.searchParams.set('maxResults', String(options?.maxResults ?? 100));
      if (pageToken) {
        url.searchParams.set('pageToken', pageToken);
      }
      if (options?.labelsToSync && options.labelsToSync.length > 0) {
        url.searchParams.set('labelIds', options.labelsToSync.join(','));
      }

      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to list messages: ${response.statusText}`);
      }

      const listData: GmailListMessagesResponse = await response.json();

      // Fetch full details for each message
      for (const messageRef of listData.messages || []) {
        try {
          const messageResponse = await fetch(
            `${this.GMAIL_API_BASE}/users/me/messages/${messageRef.id}?format=full`,
            {
              headers: { 'Authorization': `Bearer ${accessToken}` },
            }
          );

          if (messageResponse.ok) {
            const messageData: GmailMessageResponse = await messageResponse.json();
            await this.storeMessage(userId, messageData);
            synced++;
          } else {
            failed++;
          }
        } catch (error) {
          logger.error('Failed to fetch message', { messageId: messageRef.id, error });
          failed++;
        }
      }

      pageToken = listData.nextPageToken;
    } while (pageToken);

    return { synced, failed };
  }

  private async performIncrementalSync(
    userId: string,
    accessToken: string,
    startHistoryId: string,
    options?: GmailSyncOptions
  ): Promise<{ synced: number; failed: number }> {
    let synced = 0;
    let failed = 0;
    let pageToken: string | undefined;
    let currentHistoryId = startHistoryId;

    do {
      const url = new URL(`${this.GMAIL_API_BASE}/users/me/history`);
      url.searchParams.set('startHistoryId', currentHistoryId);
      if (pageToken) {
        url.searchParams.set('pageToken', pageToken);
      }
      if (options?.labelsToSync && options.labelsToSync.length > 0) {
        url.searchParams.set('labelId', options.labelsToSync.join(','));
      }

      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch history: ${response.statusText}`);
      }

      const historyData: GmailHistoryResponse = await response.json();

      // Process history records
      for (const record of historyData.history || []) {
        if (record.messagesAdded) {
          for (const { message } of record.messagesAdded) {
            try {
              await this.storeMessage(userId, message);
              synced++;
            } catch (_error) {
              failed++;
            }
          }
        }

        if (record.messagesDeleted) {
          for (const { message } of record.messagesDeleted) {
            await this.supabase
              .from('gmail_messages')
              .update({ is_trash: true })
              .eq('user_id', userId)
              .eq('gmail_message_id', message.id);
          }
        }

        if (record.labelsAdded) {
          for (const { message, labelIds } of record.labelsAdded) {
            // Update labels in database
            const { data: existing } = await this.supabase
              .from('gmail_messages')
              .select('labels')
              .eq('user_id', userId)
              .eq('gmail_message_id', message.id)
              .single();

            if (existing) {
              const updatedLabels = [...new Set([...(existing.labels ?? []), ...labelIds])];
              await this.supabase
                .from('gmail_messages')
                .update({ labels: updatedLabels })
                .eq('user_id', userId)
                .eq('gmail_message_id', message.id);
            }
          }
        }

        if (record.labelsRemoved) {
          for (const { message, labelIds } of record.labelsRemoved) {
            // Update labels in database
            const { data: existing } = await this.supabase
              .from('gmail_messages')
              .select('labels')
              .eq('user_id', userId)
              .eq('gmail_message_id', message.id)
              .single();

            if (existing) {
              const updatedLabels = (existing.labels ?? []).filter(l => !labelIds.includes(l));
              await this.supabase
                .from('gmail_messages')
                .update({ labels: updatedLabels })
                .eq('user_id', userId)
                .eq('gmail_message_id', message.id);
            }
          }
        }
      }

      currentHistoryId = historyData.historyId;
      pageToken = historyData.nextPageToken;
    } while (pageToken);

    // Update history ID for next sync
    await this.supabase
      .from('gmail_sync_state')
      .update({ history_id: currentHistoryId })
      .eq('user_id', userId);

    return { synced, failed };
  }

  private async storeMessage(userId: string, message: GmailMessageResponse): Promise<void> {
    const parsed = this.parseGmailMessage(message);

    const messageData: GmailMessageInsert = {
      user_id: userId,
      gmail_message_id: parsed.id,
      gmail_thread_id: parsed.threadId,
      from_email: parsed.from.email || '',
      from_name: parsed.from.name ?? null,
      to_emails: parsed.to.map(e => e.email),
      cc_emails: parsed.cc.map(e => e.email),
      bcc_emails: parsed.bcc.map(e => e.email),
      subject: parsed.subject,
      snippet: parsed.snippet,
      body_text: parsed.bodyText,
      body_html: parsed.bodyHtml,
      labels: parsed.labels,
      has_attachments: parsed.hasAttachments,
      attachment_count: parsed.attachments.length,
      is_read: !parsed.labels.includes('UNREAD'),
      is_starred: parsed.isStarred,
      is_important: parsed.isImportant,
      is_draft: parsed.labels.includes('DRAFT'),
      is_sent: parsed.labels.includes('SENT'),
      is_trash: parsed.labels.includes('TRASH'),
      received_at: parsed.date.toISOString(),
      sent_at: parsed.date.toISOString(),
    };

    await this.supabase
      .from('gmail_messages')
      .upsert([messageData], {
        onConflict: 'user_id,gmail_message_id'
      });

    // Store attachments
    for (const attachment of parsed.attachments) {
      await this.supabase
        .from('gmail_attachments')
        .upsert({
          user_id: userId,
          message_id: messageData.gmail_message_id as string, // Will be resolved by FK
          gmail_attachment_id: attachment.attachmentId,
          filename: attachment.filename,
          mime_type: attachment.mimeType,
          size_bytes: attachment.size,
        }, {
          onConflict: 'message_id,gmail_attachment_id'
        });
    }

    // Log message received activity
    await this.logGmailActivity(userId, parsed.id, 'received', 'gmail.message_received', {
      has_attachments: parsed.hasAttachments,
      attachment_count: parsed.attachments.length,
    });
  }

  private parseGmailMessage(message: GmailMessageResponse): ParsedGmailMessage {
    const headers = message.payload.headers;
    const getHeader = (name: string): string => {
      const header = headers.find(h => h.name.toLowerCase() === name.toLowerCase());
      return header?.value ?? '';
    };

    const parseEmailAddress = (headerValue: string): EmailAddress => {
      const match = headerValue.match(/^(?:"?([^"]*)"?\s)?<?([^>]+)>?$/);
      if (match) {
        return { name: match[1]?.trim(), email: match[2].trim() };
      }
      return { email: headerValue.trim() };
    };

    const parseEmailList = (headerValue: string): EmailAddress[] => {
      if (!headerValue) {return [];}
      return headerValue.split(',').map(e => parseEmailAddress(e.trim()));
    };

    const getBody = (payload: GmailMessagePayload, mimeType: string): string => {
      if (payload.mimeType === mimeType && payload.body?.data) {
        return atob(payload.body.data.replace(/-/g, '+').replace(/_/g, '/'));
      }
      if (payload.parts) {
        for (const part of payload.parts) {
          const body = getBody(part, mimeType);
          if (body) {return body;}
        }
      }
      return '';
    };

    const getAttachments = (payload: GmailMessagePayload): Array<{ attachmentId: string; filename: string; mimeType: string; size: number }> => {
      const attachments: Array<{ attachmentId: string; filename: string; mimeType: string; size: number }> = [];

      const extractAttachments = (part: GmailMessagePayload) => {
        if (part.filename && part.body?.attachmentId) {
          attachments.push({
            attachmentId: part.body.attachmentId,
            filename: part.filename,
            mimeType: part.mimeType,
            size: part.body.size,
          });
        }
        if (part.parts) {
          part.parts.forEach(extractAttachments);
        }
      };

      extractAttachments(payload);
      return attachments;
    };

    return {
      id: message.id,
      threadId: message.threadId,
      from: parseEmailAddress(getHeader('From')),
      to: parseEmailList(getHeader('To')),
      cc: parseEmailList(getHeader('Cc')),
      bcc: parseEmailList(getHeader('Bcc')),
      subject: getHeader('Subject'),
      snippet: message.snippet,
      bodyText: getBody(message.payload, 'text/plain'),
      bodyHtml: getBody(message.payload, 'text/html'),
      labels: message.labelIds || [],
      hasAttachments: (message.payload.parts?.some(p => p.filename) ?? false),
      attachments: getAttachments(message.payload),
      date: new Date(parseInt(message.internalDate)),
      isRead: !message.labelIds?.includes('UNREAD'),
      isStarred: message.labelIds?.includes('STARRED') || false,
      isImportant: message.labelIds?.includes('IMPORTANT') || false,
    };
  }

  /**
   * Helper: Log Gmail activity and trigger webhooks
   */
  private async logGmailActivity(
    userId: string,
    messageId: string,
    actionType: string,
    eventType: string,
    additionalDetails: Record<string, unknown>
  ): Promise<void> {
    try {
      // Get message details if available
      const { data: messageData } = await this.supabase
        .from('gmail_messages')
        .select('from_email, to_emails, subject, labels, gmail_thread_id')
        .eq('user_id', userId)
        .eq('gmail_message_id', messageId)
        .single();

      await integrationActivityService.logGmailActivity({
        userId,
        messageId,
        actionType,
        eventType,
        fromAddress: messageData?.from_email ?? undefined,
        toAddresses: messageData?.to_emails ?? undefined,
        subject: messageData?.subject ?? undefined,
        labels: messageData?.labels ?? undefined,
        threadId: messageData?.gmail_thread_id ?? undefined,
        details: additionalDetails,
      });
    } catch (error) {
      // Don't fail the main operation if activity logging fails
      logger.error('Failed to log Gmail activity', { error, userId, messageId, actionType });
    }
  }
}

// Export singleton
export const gmailClientService = new GmailClientService();
